import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp';
import {
    PaletteMode,
    PaletteOptions,
    ThemeOptions,
    alpha,
    darken,
    lighten,
} from '@mui/material';
const lightThemeTokens: PaletteOptions = {
    primary: {
        light: '#4dc0ce',
        main: '#00a5b9',
        dark: '#007382',
        contrastText: '#01444d',
    },
    secondary: {
        light: '#f6ad33',
        main: '#f49800',
        dark: '#c37a00',
        contrastText: '#000000',
    },
    text: {
        primary: '#2c2c2c',
        secondary: '#383838',
    },
    background: {
        paper: '#ffffff',
        default: '#ffffff',
    },
    action: {
        active: '#444444',
    },
};

const darkThemeTokens: PaletteOptions = {
    primary: {
        light: '#4dc0ce',
        main: '#00a5b9',
        dark: '#007382',
        contrastText: '#01444d',
    },
    secondary: {
        light: '#f6ad33',
        main: '#f49800',
        dark: '#c37a00',
        contrastText: '#ffffff',
    },
    background: {
        paper: '#1c262d',
        default: '#1c262d',
    },
    text: {
        primary: '#fff',
        secondary: '#bbb',
    },
};

export const getDesignTokens = (mode: PaletteMode): ThemeOptions => ({
    components: {
        MuiTooltip: {
            styleOverrides: {
                tooltip: ({ theme }) => ({
                    backgroundColor: alpha(
                        lighten(theme.palette.background.default, 0.1),
                        0.95
                    ),
                }),
            },
        },
        MuiTableRow: {
            styleOverrides: {
                root: {
                    '&:last-child td': {
                        borderBottom: 0,
                    },
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: ({ theme }) => ({
                    paddingTop: theme.spacing(0.5),
                    paddingBottom: theme.spacing(0.5),
                    paddingLeft: theme.spacing(1),
                    paddingRight: theme.spacing(1),
                    fontSize: 12,
                }),

                head: ({ theme }) => ({
                    backgroundColor: darken(
                        theme.palette.background.default,
                        0.1
                    ),
                }),
            },
        },

        MuiInput: {
            styleOverrides: {
                input: {
                    paddingTop: 0,
                },
            },
        },

        MuiToolbar: {
            styleOverrides: {
                root: ({ theme }) => ({
                    [theme.breakpoints.up('md')]: {
                        paddingLeft: theme.spacing(2),
                        paddingRight: theme.spacing(2),
                    },
                }),
            },
        },
        MuiSlider: {
            styleOverrides: {
                root: {
                    padding: 0,
                },
                rail: {
                    borderRadius: 4,
                },
                track: {
                    height: 4,
                    borderRadius: 4,
                },
                thumb: ({ theme }) => ({
                    height: 16,
                    width: 16,
                    background: theme.palette.background.default,
                    border: `2px solid ${theme.palette.primary.main}`,
                    '&:hover, &.Mui-active, &.Mui-focusVisible': {
                        boxShadow: 'inherit',
                    },
                }),
            },
        },
        MuiCollapse: {
            styleOverrides: {
                root: {
                    '.MuiTreeItem-group': {
                        marginLeft: 8,
                    },
                },
            },
        },
        MuiToggleButton: {
            styleOverrides: {
                root: ({ theme }) => ({
                    borderRadius: theme.shape.borderRadius,
                    ':disabled': {
                        color: 'gray',
                    },
                }),

                sizeSmall: {
                    padding: '4px 8px',
                    fontSize: '0.75rem',
                    height: '32px',
                },
                sizeMedium: {
                    padding: '4px 8px',
                    height: '36px',
                },
            },
        },

        MuiList: {
            styleOverrides: {
                root: ({ theme }) => ({
                    paddingTop: 0,
                    paddingBottom: 0,
                    '& li': {
                        '&::marker': {
                            color: theme.palette.primary.main,
                        },
                    },
                }),
            },
        },

        MuiCssBaseline: {
            styleOverrides: {
                '::selection': {
                    backgroundColor: '#00a5b9',
                    color: '#ffffff',
                },
                '::-webkit-selection': {
                    backgroundColor: '#00a5b9',
                    color: '#ffffff',
                },
                body: {
                    '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
                        backgroundColor: 'transparent',
                        width: 5,
                        height: 6,
                    },
                    '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb':
                        {
                            backgroundColor: '#6b6b6b',
                            minHeight: 36,
                            width: 1,
                        },
                    '&::-webkit-scrollbar-thumb:focus, & *::-webkit-scrollbar-thumb:focus':
                        {
                            backgroundColor: '#959595',
                        },
                    '&::-webkit-scrollbar-thumb:active, & *::-webkit-scrollbar-thumb:active':
                        {
                            backgroundColor: '#959595',
                        },
                    '&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover':
                        {
                            backgroundColor: '#959595',
                        },
                    '&::-webkit-scrollbar-corner, & *::-webkit-scrollbar-corner':
                        {
                            backgroundColor: '#2b2b2b',
                        },
                },
            },
        },
        MuiAccordion: {
            defaultProps: {
                disableGutters: true,
                square: true,
                elevation: 0,
            },
            styleOverrides: {
                root: ({ theme }) => ({
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    '&::before': {
                        display: 'none',
                    },
                }),
            },
        },
        MuiAccordionSummary: {
            defaultProps: {
                expandIcon: <ArrowForwardIosSharpIcon sx={{ fontSize: 14 }} />,
            },
            styleOverrides: {
                root: ({ theme }) => ({
                    border: 0,
                    backgroundColor:
                        theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, .05)'
                            : 'rgba(0, 0, 0, .03)',
                    flexDirection: 'row-reverse',
                    '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
                        transform: 'rotate(90deg)',
                    },
                    '& .MuiAccordionSummary-content': {
                        marginLeft: theme.spacing(2),
                    },
                }),
            },
        },
        MuiChip: {
            styleOverrides: {
                sizeSmall: {
                    fontSize: '0.75rem',
                    height: '24px',
                    padding: '0 8px',
                    '.MuiChip-label': {
                        paddingLeft: '6px',
                        paddingRight: '6px',
                    },
                },
            },
        },

        MuiCheckbox: {
            styleOverrides: {
                sizeMedium: {
                    padding: '6px',
                    '& .MuiSvgIcon-root': {
                        fontSize: '12px',
                        width: '18px',
                        height: '18px',
                    },
                },
                sizeSmall: {
                    padding: '4px',
                    '& .MuiSvgIcon-root': {
                        fontSize: '12px',
                        width: '14px',
                        height: '14px',
                    },
                },
            },
        },
    },
    typography: (theme) => ({
        h6: {
            fontSize: 19,
        },
        body2: {
            fontSize: 14,
            color: theme.text.secondary,
        },
        fontFamily: [
            'Poppins',
            '-apple-system',
            'BlinkMacSystemFont',
            '"Segoe UI"',
            'Roboto',
            '"Helvetica Neue"',
            'Arial',
            'sans-serif',
            '"Apple Color Emoji"',
            '"Segoe UI Emoji"',
            '"Segoe UI Symbol"',
        ].join(','),
    }),
    palette: {
        mode,
        ...(mode === 'light' ? lightThemeTokens : darkThemeTokens),
    },
    shape: {
        borderRadius: 4,
    },
});
